import { NgStyle } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  HostListener,
  inject,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Signal,
  signal,
  SimpleChanges,
} from '@angular/core';
import { MatIconModule } from '@angular/material/icon';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { first } from 'rxjs';

import { PropsOf } from '@blaze-runtime/engine-domain';
import {
  type ElementFormState,
  getBackground,
  PropertiesConfigInputFontStyleOptions,
} from '@blaze-shared/elements-domain';
import { NotificationService } from '@blaze-shared/platform-data-access';
import { ElementLoadingIndicatorComponent } from '@blaze-shared/shared-ui';
import {
  convertBlobToDataUrlFileResult,
  dataUrlToBase64,
  getFilenameForMimetype,
  getMimetypeFromBlob,
  type Nullish,
  useEmitTracker,
} from '@blaze-shared/shared-util';
import { FileResult } from '@blaze-shared/uploader';

import { ElementLabelComponent } from '../../components/element-label/element-label.component';
import { CanvasElementBaseComponent } from '../__base/base.component';

import { AudioRecorderOutput } from './audio-recorder.model';
import {
  buildAudioRecorderButtonStyles,
  buildAudioRecorderSectionStyles,
} from './audio-recorder.styles';

interface LabelElementStyle {
  color: string | undefined;
  fontFamily: string | undefined;
  fontSize: string | undefined;
  fontStyle: PropertiesConfigInputFontStyleOptions | undefined;
  fontWeight: string | undefined;
  horizontalAlign: string | undefined;
}

@UntilDestroy()
@Component({
  selector: 'be-audio-recorder',
  imports: [ElementLabelComponent, ElementLoadingIndicatorComponent, MatIconModule, NgStyle],
  templateUrl: './audio-recorder.component.html',
  styleUrls: ['./audio-recorder.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AudioRecorderComponent
  extends CanvasElementBaseComponent<'audioRecorder'>
  implements OnChanges, OnDestroy, OnInit
{
  private readonly notificationService = inject(NotificationService);

  @Input() value: Nullish<FileResult>;
  @Input() formState?: ElementFormState;

  private readonly dataValue = this.getDataInput('value');
  private readonly outputEmitTracker = useEmitTracker();
  private startSound: HTMLAudioElement;
  private stopSound: HTMLAudioElement;

  private readonly _props = signal<PropsOf<'audioRecorder'> | undefined>(undefined);
  private readonly recordTimeSeconds = signal<number>(0);
  private audioChunks: Blob[] = [];
  private mediaRecorder: MediaRecorder | null = null;
  private onHoldRecordingInProgress = false;
  private pendingTimeout: ReturnType<typeof setTimeout> | undefined;
  private stream: MediaStream | null = null;

  protected readonly buttonIcon = signal<string>('');
  protected readonly buttonLabel = signal<string>('');
  protected readonly isAudioStreamInitializing = signal(false);
  protected readonly isLoaderVisible = signal(false);
  protected readonly isRecording = signal(false);

  protected buttonStyles: Signal<Record<string, string | undefined>>;
  protected labelStyles: Signal<LabelElementStyle>;
  protected recordMode: Signal<'click' | 'hold'>;
  protected recordTime: Signal<string>;
  protected recordTimer: ReturnType<typeof setInterval> | undefined;
  protected recordTimeStyles: Signal<Record<string, string | undefined>>;
  protected sectionStyles: Signal<Record<string, string | undefined>>;
  protected validationStyles!: Record<string, string>;

  get hasValidationError(): boolean | undefined {
    return this.formState?.errors?.required || !!this.formState?.errors?.invalid;
  }

  get isFileSecure(): boolean {
    return this.dataValue.column?.isSecure ?? false;
  }

  constructor() {
    super();
    this.initMarginHandler((props) => props?.style?.general?.offset?.margin);
    this.initRecorderSignals();
    this.initStyleSignals();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes?.['props']) {
      this._props.set(changes['props'].currentValue);
      const { button } = this.props?.content ?? {};
      this.buttonLabel.set(button?.label ?? '');
      this.buttonIcon.set(button?.icon ?? '');
      this.updateStyles();
    }
  }

  async ngOnInit(): Promise<void> {
    this.setSchemaToEmit();
    await this.preloadAudio();
    if (!this.bindingResults?.['value']) {
      this.updateOutput(this.value);
    }

    this.initPendingListener();
  }

  ngOnDestroy(): void {
    this.stop(false);
  }

  emitClick(toolbarTriggered = false): void {
    if (this.isInEditor && !toolbarTriggered) {
      return;
    }
    if (this.recordMode() === 'hold') {
      if (this.mediaRecorder) {
        // Editor case where user clicked record and then changed the record mode to hold without stopping the recording.
        this.stop();
      } else if (!this.isInEditor) {
        // When in editor, this return will be ignored and the click action will proceed. This is to allow
        // user recording in editor mode even when they set recordMode to "hold".
        return;
      }
    }

    if (this.mediaRecorder) {
      this.stop();
    } else {
      this.record();
    }
  }

  protected onCancelRecording(): void {
    if (this.mediaRecorder) {
      // Remove onstop handling so that workflow doesn't execute and stop the recoridng.
      // eslint-disable-next-line @typescript-eslint/no-empty-function
      this.mediaRecorder.onstop = (): void => {};
    }
    this.stop();
  }

  protected onMouseDownStartRecording(event: MouseEvent | TouchEvent): void {
    if (this.isInEditor || this.recordMode() === 'click') {
      // "On hold" in editor has to behave the same way as "on click". We ignore the mouse down event and let the
      // emitClick take action.
      return;
    }
    event.preventDefault(); // prevent button highlight blinking when doing a long touch on mobile
    this.onHoldRecordingInProgress = true;

    this.record().then(() => {
      if (!this.onHoldRecordingInProgress) {
        // When user very quickly starts and stops onHold recording, it can happen that the recording may not
        // have been initialized and already needs to be stopped. We detect this case by checking the flag above.
        // When that happens, we need to stop the recording.
        this.stop();
      }
    });
  }

  @HostListener('window:touchend')
  @HostListener('window:mouseup')
  protected onMouseUpStopRecording(): void {
    if (this.recordMode() === 'click' || !this.onHoldRecordingInProgress) {
      return;
    }
    this.stop();
  }

  /**
   * Preloads audio files so that there is no delay when user starts/stops recording.
   */
  private async preloadAudio(): Promise<void> {
    let response = await fetch(`assets/beep-record-start.mp3`);
    let blob = await response.blob();
    const startSoundLocalUrl = URL.createObjectURL(blob);
    this.startSound = new Audio(startSoundLocalUrl);

    response = await fetch(`assets/beep-record-stop.mp3`);
    blob = await response.blob();
    const stopSoundLocalUrl = URL.createObjectURL(blob);
    this.stopSound = new Audio(stopSoundLocalUrl);
  }

  private updateOutput(data: Nullish<FileResult>, forceUpdate = false): void {
    const isInitialData = !this.outputEmitTracker.hasPrevious();
    if (!data?.content) {
      console.warn(`No audio recorded. Emitting output will be skipped.`, data);
      return;
    }
    // shouldEmit check must be called before checking forceUpdate to ensure hashes inside that function are properly updated
    if (this.outputEmitTracker.shouldEmit(data) || forceUpdate) {
      this.value = data;
      this.output.emit({
        eventType: 'change',
        data,
        isInitialData,
        userUpdate: forceUpdate,
        schema: this.getEventSchema('change'),
      });
    }
    this.cdr.detectChanges();
  }

  private setSchemaToEmit(): void {
    this.setDefaultEventSchema('change');
  }

  private updateStyles(): void {
    const { style, content } = this.props ?? {};
    const { title } = content ?? {};
    const { background, button, label, general } = style ?? {};
    if (!style || !background || !button || !general || !label || !title) {
      return;
    }
    const validationStyles: Record<string, string> = {};
    validationStyles['margin-left'] = title.width;
    validationStyles['padding-left'] = '8px';
    this.validationStyles = validationStyles;
  }

  /**
   * Creates a blob from collected media chunks, converts it to base64 and updates component output.
   */
  private convertMediaToBase64AndUpdateOutput(): void {
    const mimetype = getMimetypeFromBlob(this.audioChunks[0]);
    const filename = getFilenameForMimetype('file', mimetype);
    const audioFileBlob = new Blob(this.audioChunks, { type: mimetype });
    this.audioChunks = [];
    console.log('Recording audio with mimetype', mimetype);

    convertBlobToDataUrlFileResult(audioFileBlob, filename)
      .pipe(first())
      .subscribe(({ url, filename, mimetype, size }) => {
        const content = dataUrlToBase64(url);

        if (!url || !filename || !mimetype) {
          console.error(`Unable to update audio recorder output. Missing file data:`, {
            content,
            filename,
            mimetype,
            size,
          });
          return;
        }
        const output: AudioRecorderOutput = {
          content,
          filename,
          mimetype,
          size,
        };
        this.updateOutput(output, true);
      });
  }

  private createMediaRecorder(stream: MediaStream): MediaRecorder | null {
    if (!stream) {
      return null;
    }

    const mediaRecorder = new MediaRecorder(stream);
    mediaRecorder.ondataavailable = (e): void => {
      this.audioChunks.push(e.data);
    };
    mediaRecorder.onstop = this.convertMediaToBase64AndUpdateOutput.bind(this);
    mediaRecorder.onerror = this.stop.bind(this, false);

    return mediaRecorder;
  }

  private getAudioStream(): Promise<void | MediaStream> {
    if (!navigator.mediaDevices?.getUserMedia) {
      this.notificationService.error('Audio recording is not supported on your browser');
      this.stop();
      return Promise.resolve();
    }

    return navigator.mediaDevices
      .getUserMedia({ audio: true })
      .then((stream) => {
        return stream;
      })
      .catch((err) => {
        this.notificationService.error(err);
        this.stop();
        return undefined;
      });
  }

  private initPendingListener(): void {
    this.isPending$.pipe(untilDestroyed(this)).subscribe((isPending) => {
      if (!isPending) {
        clearTimeout(this.pendingTimeout);
        this.isLoaderVisible.set(false);
        return;
      }
      this.pendingTimeout = setTimeout(() => {
        this.isLoaderVisible.set(true);
      }, 3000);
    });
  }

  private initRecorderSignals(): void {
    this.recordMode = computed(() => {
      return this._props()?.content?.options?.recordMode ?? 'click';
    });

    this.recordTime = computed(() => {
      const minutes = Math.floor(this.recordTimeSeconds() / 60);
      const seconds = this.recordTimeSeconds() % 60;
      if (seconds < 10) {
        return `${minutes}:0${seconds}`;
      }
      return `${minutes}:${seconds}`;
    });
  }

  private initStyleSignals(): void {
    this.buttonStyles = computed(() => {
      const button = this._props()?.style?.button;
      if (!button) {
        return {};
      }

      const mode = this.isRecording() ? 'recording' : 'idle';
      return buildAudioRecorderButtonStyles(button, mode);
    });

    this.labelStyles = computed(() => {
      const { general, label } = this._props()?.style || {};

      const mode = this.isRecording() ? 'recording' : 'idle';
      return {
        color: label?.[mode].color ? getBackground(label?.[mode].color) : general?.title?.color1,
        fontFamily: label?.font?.fontFamily,
        fontSize: label?.font?.fontSize,
        fontStyle: label?.[mode].textStyle,
        fontWeight: label?.[mode].fontWeight,
        horizontalAlign: this.props?.content?.title?.align,
      };
    });

    this.recordTimeStyles = computed(() => {
      const { label } = this._props()?.style || {};
      if (!label) {
        return {};
      }
      const mode = this.isRecording() ? 'recording' : 'idle';
      return {
        color: getBackground(label[mode].color),
      };
    });

    this.sectionStyles = computed(() => {
      const background = this._props()?.style?.background;
      if (!background) {
        return {};
      }

      const mode = this.isRecording() ? 'recording' : 'idle';
      return buildAudioRecorderSectionStyles(background, mode);
    });
  }

  private async playSound(soundType: 'start' | 'stop'): Promise<void> {
    if (soundType === 'start') {
      await this.startSound
        ?.play()
        .catch((err) => console.error('Error playing start sound:', err));
    } else if (soundType === 'stop') {
      await this.stopSound?.play().catch((err) => console.error('Error playing start sound:', err));
    }
  }

  private record(): Promise<void> {
    const { button } = this.props?.content ?? {};
    this.isAudioStreamInitializing.set(true);

    return this.getAudioStream()
      .then(async (stream) => {
        if (!stream) {
          this.stop();
          this.isAudioStreamInitializing.set(false);
          this.isRecording.set(false);
          return;
        }

        this.mediaRecorder = this.createMediaRecorder(stream);
        this.stream = stream;

        console.log(`Starting audio recording`);
        await this.playSound('start');
        this.isRecording.set(true);
        this.buttonLabel.set(button?.labelStop ?? '');
        this.buttonIcon.set(button?.iconStop ?? '');
        this.isAudioStreamInitializing.set(false);
        this.mediaRecorder?.start();

        this.recordTimer = setInterval(() => {
          this.recordTimeSeconds.set(this.recordTimeSeconds() + 1);
        }, 1000);
      })
      .catch(() => {
        this.isAudioStreamInitializing.set(false);
        this.stop();
      });
  }

  private stop(playSoundOnStop = true): void {
    this.onHoldRecordingInProgress = false;
    const { button } = this.props?.content ?? {};

    if (this.mediaRecorder) {
      console.log(`Stopping audio recording`);
      this.mediaRecorder.stop();
    }
    // Stop each track in the stream to ensure the browser icon indicating microphone use disappears.
    if (this.stream) {
      this.stream.getTracks().forEach((track) => {
        track.stop();
      });
    }
    this.stream = null;
    this.mediaRecorder = null;

    this.isRecording.set(false);
    this.recordTimeSeconds.set(0);
    this.buttonLabel.set(button?.label || '');
    this.buttonIcon.set(button?.icon || '');
    clearInterval(this.recordTimer);
    if (playSoundOnStop) {
      this.playSound('stop');
    }
  }
}
